import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { Button, Switch, Dialog, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { UpdateSettingsBody } from '../../utils/types';
import { AppStore } from '../../AppStore';
import { PathwordStore } from '../_puzzles/Pathword/PathwordStore';
import { Pathword5x5Store } from '../_puzzles/Pathword5x5/Pathword5x5Store';
import { getEnabledHintsForPathword, getEnabledHintsForPathword5x5 } from '../_puzzles/Pathword/pathwordUtils';
import './AccountPage.less';

const AccountPage = () => {
  const navigate = useNavigate();
  const initialSettings = AppStore.useState(s => s.userData?.settings) || {} as any;

  const auth = getAuth();
  const signedIn = AppStore.useState(s => s.signedIn);
  const [hfEnabled, setHfEnabled] = useState(initialSettings.hapticFeedbackEnabled);
  const [pathwordHints, setPathwordHints] = useState(initialSettings.pathwordDefaultHints ?? 2);
  const [pathword5x5Hints, setPathword5x5Hints] = useState(initialSettings.pathword5x5DefaultHints ?? 4);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (!signedIn) {
      navigate('/');
      AppStore.update(s => {
        s.signInDialog.isOpen = true;
        s.signInDialog.redirectPathAfterLogin = '/account';
      });
    }
  });

  const saveChanges = async (settings: { hapticFeedbackEnabled?: boolean; pathwordDefaultHints?: number; pathword5x5DefaultHints?: number }) => {
    try {
      const authToken = await auth.currentUser?.getIdToken(true);
      await axios.post<any>(
        '/api/users/update-settings', {
        authToken,
        newSettings: settings,
      } as UpdateSettingsBody, {
        timeout: 5000, // 5 second timeout
      });

      // Only update the store after successful API response
      AppStore.update(s => {
        if (settings.hapticFeedbackEnabled !== undefined) {
          s.userData!.settings.hapticFeedbackEnabled = settings.hapticFeedbackEnabled;
        }
        if (settings.pathwordDefaultHints !== undefined) {
          s.userData!.settings.pathwordDefaultHints = settings.pathwordDefaultHints;
        }
        if (settings.pathword5x5DefaultHints !== undefined) {
          s.userData!.settings.pathword5x5DefaultHints = settings.pathword5x5DefaultHints;
        }
      });

      // Success - show success toast
      setToastMessage('Changes saved');
      setToastSeverity('success');
      setToastOpen(true);
    } catch (error) {
      console.error('Error saving settings:', error);

      // Revert local state on failure
      if (settings.hapticFeedbackEnabled !== undefined) {
        setHfEnabled(initialSettings.hapticFeedbackEnabled);
      }
      if (settings.pathwordDefaultHints !== undefined) {
        setPathwordHints(initialSettings.pathwordDefaultHints ?? 2);
        PathwordStore.update(s => {
          s.enabledHints = getEnabledHintsForPathword(initialSettings.pathwordDefaultHints ?? 2);
          s.initialEnabledHints = initialSettings.pathwordDefaultHints ?? 2;
        });
      }
      if (settings.pathword5x5DefaultHints !== undefined) {
        setPathword5x5Hints(initialSettings.pathword5x5DefaultHints ?? 4);
        Pathword5x5Store.update(s => {
          s.enabledHints = getEnabledHintsForPathword5x5(initialSettings.pathword5x5DefaultHints ?? 4);
          s.initialEnabledHints = initialSettings.pathword5x5DefaultHints ?? 4;
        });
      }

      // Error - show error toast
      setToastMessage('Failed to save changes. Please try again.');
      setToastSeverity('error');
      setToastOpen(true);
    }
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const authToken = await auth.currentUser?.getIdToken(true);
      await axios.post('/api/users/delete-account', { authToken }, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Clear local storage
      localStorage.clear();

      // Sign out from Firebase
      await auth.signOut();

      // Redirect to home
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      alert('Failed to delete account. Please try again or contact support.');
    }
  };

  return (
    <div className="accountPage">

      <h2>Account Settings</h2>

      {/* if the site succeeds we can implement this stuff later on */}

      {/* <div className="settingsSection">
        <Button variant="outlined">Update Password</Button>
      </div>

      <div className="settingsSection">
        <Button variant="outlined">Update Email</Button>
      </div> */}

      <div className="settingsSection">
        <span>Enable haptic feedback for mobile keyboard</span>
        <Switch
          checked={hfEnabled}
          onChange={() => {
            setHfEnabled(!hfEnabled);
            saveChanges({ hapticFeedbackEnabled: !hfEnabled });
          }}
          inputProps={{ 'aria-label': 'controlled' }}
        />
      </div>

      <div className="settingsSection">
        <FormControl>
          <FormLabel>Pathword Default Hints</FormLabel>
          <RadioGroup
            row
            value={pathwordHints}
            onChange={(e) => {
              const value = Number(e.target.value);
              setPathwordHints(value);

              // Update the store immediately
              PathwordStore.update(s => {
                s.enabledHints = getEnabledHintsForPathword(value);
                s.initialEnabledHints = value;
                s.hintsUsed = 0;
              });

              saveChanges({ pathwordDefaultHints: value });
            }}
          >
            <FormControlLabel value={0} control={<Radio />} label="0" />
            <FormControlLabel value={1} control={<Radio />} label="1" />
            <FormControlLabel value={2} control={<Radio />} label="2" />
          </RadioGroup>
        </FormControl>
      </div>

      <div className="settingsSection">
        <FormControl>
          <FormLabel>Pathword 5x5 Default Hints</FormLabel>
          <RadioGroup
            row
            value={pathword5x5Hints}
            onChange={(e) => {
              const value = Number(e.target.value);
              setPathword5x5Hints(value);

              // Update the store immediately
              Pathword5x5Store.update(s => {
                s.enabledHints = getEnabledHintsForPathword5x5(value);
                s.initialEnabledHints = value;
                s.hintsUsed = 0;
              });

              saveChanges({ pathword5x5DefaultHints: value });
            }}
          >
            <FormControlLabel value={0} control={<Radio />} label="0" />
            <FormControlLabel value={1} control={<Radio />} label="1" />
            <FormControlLabel value={2} control={<Radio />} label="2" />
            <FormControlLabel value={3} control={<Radio />} label="3" />
            <FormControlLabel value={4} control={<Radio />} label="4" />
          </RadioGroup>
        </FormControl>
      </div>

      <div className="settingsSection">
        <Button
          variant="outlined"
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete My Account
        </Button>
      </div>

      <Button className="returnHomeButton" variant="text" onClick={() => { navigate('/'); }}>
        Return to Main Page
      </Button>

      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
        <div className="deleteConfirmationDialog">
          <h2>Delete Account</h2>
          <p>
            Are you absolutely sure you want to delete your account? This action cannot be undone.
          </p>
          <p className="warningText">
            All your progress, stats, and settings will be permanently deleted.
          </p>
          <div className="dialogButtons">
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteAccount}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Yes, Delete My Account'}
            </Button>
          </div>
        </div>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '@media (min-width: 768px)': {
            bottom: '80px !important',
            left: 'auto !important',
            right: '24px !important',
            transform: 'none !important',
          }
        }}
      >
        <Alert onClose={handleToastClose} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AccountPage;